import { ItemInstance } from '@headless-tree/core';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, DotsMenu12, Plus16, Section } from 'shared/assets/icons';
import { GoalSectionContextMenu } from 'shared/components/connected/goal-section-context-menu';
import { LifeAreaSectionContextMenu } from 'shared/components/connected/life-area-section-context-menu';
import { Icon } from 'shared/components/ui/icon';
import {
  Colors as IconButtonColors,
  IconButton,
  Sizes,
} from 'shared/components/ui/icon-button';
import { ResizableInput } from 'shared/components/ui/resizable-input';
import { InternalTreeItem } from 'shared/components/ui/virtualized-tree-list';
import { useForwardedRef } from 'shared/hooks/use-forwarded-ref';
import { useKeysDown } from 'shared/hooks/use-keys-down';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { ID } from 'shared/types/id';
import { noGroupingId } from 'shared/views/task-filter-list/types';
import { useTheme } from 'styled-components';

import * as Styled from './section-header.style';

export type SectionHeaderProps = {
  item: ItemInstance<InternalTreeItem>;
  denseFormat?: boolean;
  onUpdateSectionName?: (id: ID, name: string) => void;
  taskCount?: number;
  goalId?: ID;
  lifeAreaId?: ID;
  onAddTask?: () => void;
};

export const SectionHeader: React.FC<SectionHeaderProps> = ({
  item,
  denseFormat,
  onUpdateSectionName,
  taskCount,
  goalId,
  lifeAreaId,
  onAddTask,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const inputRef = useForwardedRef<HTMLInputElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const id = item.getItemData()?.sourceId ?? '';
  const name = item.getItemData()?.name ?? '';
  const isNoGrouping = id === noGroupingId;
  const isOpen = item.isExpanded();
  const [updatedName, setUpdatedName] = useState(name);

  const toggleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    item.isExpanded() ? item.collapse() : item.expand();
  };

  const handleAddTask = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!item.isExpanded()) {
      item.expand();
    }
    onAddTask?.();
  };

  const onChangeName = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    setUpdatedName(target.value);
  };

  const onBlurName = () => {
    if (updatedName !== name) {
      onUpdateSectionName?.(id, updatedName);
    }
  };

  const [menuPosition, setMenuPosition] = useState<
    { left: number; top: number } | undefined
  >(undefined);
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();

  const preventPropagation = (e: React.MouseEvent) => e.stopPropagation();

  const onContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!goalId && !lifeAreaId) {
      return;
    }

    setMenuPosition({ left: e.clientX, top: e.clientY });
    openMenu();
  };

  const onMoreButton = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setMenuPosition(undefined);
    openMenu();
  };

  // If item data updates (e.g. name changed externally), sync local state.
  useEffect(() => {
    setUpdatedName(name);
  }, [name]);

  useKeysDown(
    ['Enter'],
    (e) => {
      if (document.activeElement === inputRef.current) {
        e.preventDefault();
        inputRef.current?.blur();
      }
    },
    { otherKeysInCombinationAllowed: true },
  );

  return (
    <>
      <Styled.Container
        onClick={toggleOpen}
        $denseFormat={denseFormat}
        onContextMenu={
          !isNoGrouping && (goalId || lifeAreaId) && !theme.isMobile
            ? onContextMenu
            : undefined
        }
      >
        <Styled.Body>
          <Styled.IconContainer>
            <Icon icon={Section} />
          </Styled.IconContainer>
          <Styled.Content>
            {!isNoGrouping ? (
              <ResizableInput
                ref={inputRef}
                value={updatedName}
                onChange={onChangeName}
                onBlur={onBlurName}
                onClick={preventPropagation}
                minWidth={'0.2rem'}
                placeholder={t(
                  'general.labels.goal.sections.placeholder.label',
                )}
                inheritFont
              />
            ) : (
              <Styled.DefaultTitle>{name}</Styled.DefaultTitle>
            )}
            {typeof taskCount === 'number' && (
              <Styled.TaskCount>{taskCount}</Styled.TaskCount>
            )}
          </Styled.Content>
          {!isNoGrouping && (goalId || lifeAreaId) && (
            <Styled.IconButtonContainer>
              <IconButton
                ref={buttonRef}
                icon={DotsMenu12}
                onClick={onMoreButton}
                size={Sizes.ExtraSmall}
                color={IconButtonColors.Default}
              />
            </Styled.IconButtonContainer>
          )}
        </Styled.Body>
        <Styled.HoverVisible>
          <IconButton
            icon={Plus16}
            onClick={handleAddTask}
            color={IconButtonColors.Default}
            size={Sizes.Small}
            as="span"
          />
        </Styled.HoverVisible>
        <IconButton
          icon={ChevronLeft}
          onClick={toggleOpen}
          rotation={isOpen ? -90 : 0}
          color={IconButtonColors.Default}
          size={Sizes.Small}
          as="span"
        />
      </Styled.Container>

      {menuOpen && !!goalId && (
        <GoalSectionContextMenu
          id={id}
          goalId={goalId}
          inputRef={inputRef}
          referenceElement={menuPosition ? undefined : buttonRef}
          location={menuPosition}
          onClose={closeMenu}
        />
      )}
      {menuOpen && !!lifeAreaId && (
        <LifeAreaSectionContextMenu
          id={id}
          lifeAreaId={lifeAreaId}
          inputRef={inputRef}
          referenceElement={menuPosition ? undefined : buttonRef}
          location={menuPosition}
          onClose={closeMenu}
        />
      )}
    </>
  );
};
